/* ── Cage Surround Indicator ──────────────────────────────────────
 * Semi-transparent wireframe sphere centered on the enemy drone
 * currently slowed by CageSurround. Server publishes the chosen
 * drone id on `player.cageTargetId`; this renderer looks it up
 * against the interpolated drones each frame.
 *
 * Sphere radius matches CAGE_SURROUND_RADIUS (10u) on the server
 * — see SimulationLoop's CAGE_SURROUND_RADIUS constant.
 *
 * Public API matches the project's renderer convention:
 *   - constructor adds the sphere to the scene
 *   - update(deltaMs, target) animates and repositions
 *   - dispose() cleans up
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface CageSurroundTarget {
  position: Vec3;
}

/** Matches CAGE_SURROUND_RADIUS in src/server/simulation/SimulationLoop.ts. */
const CAGE_RADIUS = 10;
const ROTATION_SPEED = 0.4;
const CAGE_COLOR = 0x9b6bff;

export class CageSurroundIndicator {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly geometry: THREE.SphereGeometry;
  private elapsed = 0;

  constructor(scene: THREE.Object3D) {
    this.geometry = new THREE.SphereGeometry(CAGE_RADIUS, 16, 12);
    this.material = new THREE.MeshBasicMaterial({
      color: CAGE_COLOR,
      transparent: true,
      opacity: 0.18,
      wireframe: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.renderOrder = 8;
    scene.add(this.mesh);
  }

  /**
   * Update the cage indicator for the current frame. Pass the resolved
   * cage target (or null when CageSurround is inactive / nothing enclosed).
   */
  update(deltaMs: number, target: CageSurroundTarget | null): void {
    if (!target) {
      if (this.mesh.visible) this.mesh.visible = false;
      return;
    }
    this.elapsed += deltaMs / 1000;
    this.mesh.position.set(target.position.x, target.position.y, target.position.z);
    this.mesh.rotation.y = this.elapsed * ROTATION_SPEED;
    this.mesh.visible = true;
  }

  dispose(): void {
    if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
